import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Heart,
  Percent,
  Gift,
  MapPin,
  Trash,
} from "@phosphor-icons/react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getLocalizedText } from "@shared/lib/utils";
import { trackEvent } from "@shared/lib/posthog-lazy";
import { motion } from "framer-motion";
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/favorites")({
  component: FavoritesPage,
});

function FavoritesPage() {
  const currentLang = getCurrentLanguage();
  const { userId, sessionToken } = getStoredUserSession();

  const favorites = useQuery(
    api.favorites.getUserFavorites,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );

  const latestOffers = useQuery(api.offers.getLatestOffers, { limit: 3 });

  const removeFavorite = useMutation(api.favorites.removeFavorite);

  const handleRemove = async (offerId: Id<"offers">) => {
    if (!userId) return;
    await removeFavorite({ userId, sessionToken: sessionToken!, offerId });

    // Găsește oferta pentru tracking
    const favorite = favorites?.find((fav) => fav.offer._id === offerId);
    await trackEvent("offer_removed_from_favorites", {
      offerId: offerId,
      partnerId: favorite?.partner?._id,
      fromPage: "favorites",
    });
  };

  if (favorites === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pt-[90px]">
      {favorites.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="relative w-full max-w-[280px] h-[200px] mb-8 flex items-center justify-center">
            {latestOffers ? (
              <>
                {/* Card stanga */}
                <motion.div
                  initial={{ rotate: -10, x: -20, opacity: 0 }}
                  animate={{ rotate: -12, x: -50, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="absolute top-0 w-[140px] h-[190px] rounded-2xl overflow-hidden shadow-xl bg-white border-4 border-white z-0"
                >
                  <img
                    src={latestOffers[1]?.imageUrl || latestOffers[0]?.imageUrl}
                    alt="Offer prev"
                    className="w-full h-full object-cover opacity-80"
                  />
                </motion.div>

                {/* Card dreapta */}
                <motion.div
                  initial={{ rotate: 10, x: 20, opacity: 0 }}
                  animate={{ rotate: 12, x: 50, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="absolute top-0 w-[140px] h-[190px] rounded-2xl overflow-hidden shadow-xl bg-white border-4 border-white z-0"
                >
                  <img
                    src={latestOffers[2]?.imageUrl || latestOffers[0]?.imageUrl}
                    alt="Offer next"
                    className="w-full h-full object-cover opacity-80"
                  />
                </motion.div>

                {/* Card centru */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="absolute top-[-10px] w-[150px] h-[210px] rounded-2xl overflow-hidden shadow-2xl bg-white border-4 border-white z-10"
                >
                  <img
                    src={latestOffers[0]?.imageUrl}
                    alt="Offer center"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                    <Heart size={16} weight="fill" className="text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-white text-xs font-bold truncate">
                      {latestOffers[0]?.partner?.name}
                    </p>
                  </div>
                </motion.div>
              </>
            ) : (
              <div className="w-16 h-16 animate-pulse bg-gray-200 rounded-full" />
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-[280px]"
          >
            <h5 className="text-xl font-bold text-text mb-3">
              {currentLang === "ro"
                ? "Nu ai favorite încă"
                : "No favorites yet"}
            </h5>
            <p className="text-text-muted mb-8 leading-relaxed">
              {currentLang === "ro"
                ? "Apasă pe inimioară la ofertele care îți plac și vor apărea aici."
                : "Tap the heart on offers you like and they will appear here."}
            </p>

            <Link
              to="/app"
              className="px-8 py-3.5 bg-primary text-white rounded-full font-semibold shadow-primary hover:shadow-primary-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {currentLang === "ro" ? "Descoperă oferte" : "Discover offers"}
            </Link>
          </motion.div>
        </div>
      ) : (
        <div className="space-y-3">
          {favorites.map((fav) => (
            <div
              key={fav._id}
              className="bg-white rounded-xl overflow-hidden shadow-sm"
            >
              <Link
                to="/app/offers/$offerId"
                params={{ offerId: fav.offer._id }}
                className="flex items-center gap-4 p-4"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  {fav.offer.discountType === "percent" ? (
                    <Percent size={20} className="text-primary" />
                  ) : (
                    <Gift size={20} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text truncate">
                    {fav.partner?.name}
                  </p>
                  <p className="text-sm text-text-light truncate">
                    {getLocalizedText(fav.offer.shortDescription || fav.offer.description, currentLang)}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-bold rounded">
                      {fav.offer.discountType === "percent"
                        ? `${fav.offer.discountValue}%`
                        : getLocalizedText(fav.offer.benefitText, currentLang)}
                    </span>
                    <span className="text-xs text-text-muted">
                      {fav.category?.name}
                    </span>
                  </div>
                </div>
              </Link>

              <div className="flex items-center justify-between mb-6 px-4 py-2 bg-gray-50 border-t border-border">
                <div className="flex items-center gap-1 text-text-muted text-sm">
                  <MapPin size={16} />
                  <span>
                    {fav.partner?.address}, {fav.partner?.city}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemove(fav.offer._id);
                  }}
                  className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
